import * as React from 'react';
import { Location, LocationWithCoordinates } from 'lib/types';
import isEqual from 'lodash/isEqual';
import { graphql, useStaticQuery } from 'gatsby';
import usePrevious from './usePrevious';

export const locationsQuery = graphql`
    fragment LocationData on Location {
        _id
        slug
        shortSlug
        name
        city {
            cityId
            label
            slug
            sortOrder
        }
        timezone
        description
        phoneNumber
        intlPhoneNumber
        openDate
        sortOrder
        coordinates {
            latitude
            longitude
        }
        fullAddress {
            streetAddress
            city
            zipCode
            state
        }
        bookingSettings {
            requiresCreditCard
            firstVisitExamPromoHtml
            hasFirstVisitPromo
            requirePetSignalmentForNewPets
            allowIndividualProviderBookings
        }
        showTeamBios
        dropOffsOnly
        bookingEnabled
        surgeryCalendlyUrl
        nearestLocations {
            distance
            location {
                _id
                name
                slug
                shortSlug
                city {
                    cityId
                    slug
                }
            }
        }
        promotion {
            description
            validUntil
        }
        city {
            id
            label
            slug
        }
        googleLocation {
            metadata {
                mapsUrl
                newReviewUrl
                placeId
            }
        }
        wellnessPromoDays
    }
    query Locations {
        allLocation {
            nodes {
                ...LocationData
            }
        }
        page(slug: { eq: "locations" }) {
            pageData
        }
    }
`;

interface LocationsQuery {
    allLocation: {
        nodes: ReadonlyArray<LocationWithCoordinates>;
    };
    page: {
        pageData?: { locationOrder?: string[] };
    };
}

export default function useLocations(
    includeBookingDisabled = true,
): ReadonlyArray<LocationWithCoordinates> {
    const {
        allLocation: { nodes: queryLocations },
        page,
    } = useStaticQuery<LocationsQuery>(locationsQuery);

    const [locations, setLocations] = React.useState(() => queryLocations);

    const previousLocations =
        usePrevious<ReadonlyArray<LocationWithCoordinates>>(queryLocations);
    React.useEffect(() => {
        if (
            !previousLocations &&
            queryLocations &&
            !isEqual(queryLocations, previousLocations)
        ) {
            setLocations(queryLocations);
        }
    }, [queryLocations, previousLocations]);

    return React.useMemo(() => {
        const unOrderedlocations = locations
            .filter(
                ({ coordinates, bookingEnabled }: Location) =>
                    !!coordinates && (includeBookingDisabled || bookingEnabled),
            )
            .map<LocationWithCoordinates>(
                ({
                    _id,
                    name,
                    timezone,
                    coordinates,
                    fullAddress,
                    phoneNumber,
                    intlPhoneNumber,
                    ...rest
                }: Location) =>
                    ({
                        _id,
                        name,
                        timezone,
                        coordinates,
                        fullAddress,
                        phoneNumber,
                        intlPhoneNumber,
                        ...rest,
                    }) as LocationWithCoordinates,
            );

        let orderedLocations: LocationWithCoordinates[] = [];

        if (page?.pageData?.locationOrder) {
            orderedLocations = [...unOrderedlocations].sort(
                (locationA, locationB) => {
                    let indexA = page.pageData?.locationOrder?.findIndex(
                        (id) => id === locationA._id,
                    );
                    let indexB = page.pageData?.locationOrder?.findIndex(
                        (id) => id === locationB._id,
                    );
                    if (indexA == null || indexA === -1) {
                        indexA = Number.MAX_SAFE_INTEGER;
                    }
                    if (indexB == null || indexB === -1) {
                        indexB = Number.MAX_SAFE_INTEGER;
                    }
                    return indexA - indexB;
                },
            );
        } else {
            orderedLocations = [...unOrderedlocations];
        }

        return orderedLocations;
    }, [locations, page?.pageData?.locationOrder, includeBookingDisabled]);
}
